import { defineComponent as _defineComponent } from 'vue';
import { BAlert, BButton, BFormCheckbox, BFormCheckboxGroup, BFormGroup, BFormTextarea, BModal, BTab, BTabs, } from "bootstrap-vue";
import { computed, ref, watch } from "vue";
import { GalaxyApi } from "@/api";
import {} from "@/components/Form/Elements/FormDrilldown/utilities";
import { useDetailedDatatypes } from "@/composables/datatypes";
import { Toast } from "@/composables/toast";
import { useDbKeyStore } from "@/stores/dbKeyStore";
import { errorMessageAsString } from "@/utils/simple-error";
import FormDrilldown from "@/components/Form/Elements/FormDrilldown/FormDrilldown.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
import SingleItemSelector from "@/components/SingleItemSelector.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'DirectoryDatasetPicker',
    props: {
        folderId: null,
        target: null
    },
    emits: ["reload", "onClose", "onSelect"],
    setup(__props, { emit }) {
        const props = __props;
        const autoExtension = {
            id: "auto",
            extension: "auto",
            text: "Auto-detect",
            description: `This system will try to detect the file type automatically.
                     If your file is not detected properly as one of the known formats,
                     it most likely means that it has some format problems (e.g., different
                     number of columns on different rows). You can still coerce the system
                     to set your data to the format you think it should be.
                     You can also upload compressed files, which will automatically be decompressed`,
            descriptionUrl: "",
        };
        const dbKeyStore = useDbKeyStore();
        const { datatypes, datatypesLoading } = useDetailedDatatypes();
        const activeTab = ref(0);
        const importing = ref(false);
        const paths = ref("");
        const options = ref([]);
        const optionsLoading = ref(false);
        const selectedDbKey = ref();
        const dbKeyList = ref([]);
        const errorMessage = ref("");
        const currentValue = ref([]);
        const preserveOptions = ref([]);
        const extensionsList = ref([]);
        const selectedExtension = ref(autoExtension);
        const pathMode = computed(() => {
            return props.target === "path";
        });
        const filesMode = computed(() => {
            return activeTab.value === 0;
        });
        const title = computed(() => {
            return pathMode.value ? "Please enter paths to import" : "Please select folders or files";
        });
        const importDisable = computed(() => {
            if (importing.value) {
                return true;
            }
            if (pathMode.value) {
                return paths.value?.length === 0;
            }
            return currentValue.value?.length === 0;
        });
        const okButtonText = computed(() => {
            const length = currentValue.value?.length || 0;
            return length === 0 ? "Import" : `Import ${length} dataset${length > 1 ? "s" : ""}`;
        });
        async function fetchOptions() {
            optionsLoading.value = true;
            const { data, error } = await GalaxyApi().GET("/api/remote_files", {
                params: {
                    query: {
                        format: "jstree",
                        target: props.target,
                        disable: filesMode.value ? "folders" : "files",
                    },
                },
            });
            options.value = mapDataToOptions(data);
            if (error) {
                errorMessage.value = errorMessageAsString(error, "Failed to load directories.");
            }
            optionsLoading.value = false;
        }
        function mapDataToOptions(data) {
            return data?.map((item) => {
                const option = {
                    name: item.text,
                    fullPath: item.li_attr.full_path,
                    value: item.li_attr.id,
                    leaf: item.type === "file",
                    disabled: item.state.disabled,
                    options: item.children ? mapDataToOptions(item.children) : [],
                };
                return option;
            });
        }
        function getFullPathById(id) {
            function traverse(nodes, targetId) {
                for (const node of nodes) {
                    if (node.value === targetId) {
                        return node.fullPath;
                    }
                    if (node.options?.length > 0) {
                        const result = traverse(node.options, targetId);
                        if (result) {
                            return result;
                        }
                    }
                }
                return "";
            }
            return traverse(options.value, id);
        }
        async function fetchExtAndDbKey() {
            extensionsList.value = datatypes.value;
            extensionsList.value.sort((a, b) => (a.extension > b.extension ? 1 : a.extension < b.extension ? -1 : 0));
            extensionsList.value = [autoExtension, ...extensionsList.value];
            selectedExtension.value = autoExtension;
            await dbKeyStore.fetchUploadDbKeys();
            dbKeyList.value = dbKeyStore.uploadDbKeys;
            selectedDbKey.value = dbKeyStore.uploadDbKeys.find((item) => item.id === "?");
            dbKeyList.value.sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0));
        }
        async function onImport() {
            if (pathMode.value) {
                const tmp = paths.value.split("\n");
                const validPaths = [];
                for (let i = tmp.length - 1; i >= 0; i--) {
                    var trimmed = tmp?.[i]?.trim();
                    if (trimmed && trimmed?.length !== 0) {
                        validPaths.push(trimmed);
                    }
                }
                if (validPaths.length === 0) {
                    Toast.error("Please provide a valid path");
                    return;
                }
                else {
                    importFileOrFolder(validPaths, "admin_path");
                }
            }
            else {
                const source = `${props.target}_${filesMode.value ? "file" : "folder"}`;
                const validPaths = currentValue.value.map((item) => getFullPathById(item));
                importFileOrFolder(validPaths, source);
            }
        }
        async function importFileOrFolder(validPaths, source) {
            const items = [];
            for (const path of validPaths) {
                const reqData = {
                    path: path,
                    source: source,
                    dbkey: selectedDbKey.value?.id || "?",
                    encoded_folder_id: props.folderId,
                    link_data: preserveOptions.value.includes("link_files"),
                    space_to_tab: preserveOptions.value.includes("space_to_tab"),
                    to_posix_lines: preserveOptions.value.includes("to_posix_lines"),
                    tag_using_filenames: preserveOptions.value.includes("tags_from_filenames"),
                };
                if (!pathMode.value) {
                    reqData.file_type = selectedExtension.value.extension;
                }
                if (!filesMode.value) {
                    reqData.preserve_dirs = preserveOptions.value.includes("preserve_directory_structure");
                }
                items.push(reqData);
            }
            emit("onSelect", items);
            emit("onClose");
        }
        watch(activeTab, () => {
            if (!pathMode.value) {
                fetchOptions();
            }
        }, { immediate: true });
        function onSelectDbKey(item) {
            selectedDbKey.value = item;
        }
        function onSelectExtension(item) {
            selectedExtension.value = item;
        }
        watch(() => datatypesLoading.value, () => {
            if (!datatypesLoading.value) {
                fetchExtAndDbKey();
            }
        });
        return { __sfc: true, autoExtension, props, emit, dbKeyStore, datatypes, datatypesLoading, activeTab, importing, paths, options, optionsLoading, selectedDbKey, dbKeyList, errorMessage, currentValue, preserveOptions, extensionsList, selectedExtension, pathMode, filesMode, title, importDisable, okButtonText, fetchOptions, mapDataToOptions, getFullPathById, fetchExtAndDbKey, onImport, importFileOrFolder, onSelectDbKey, onSelectExtension, BAlert, BButton, BFormCheckbox, BFormCheckboxGroup, BFormGroup, BFormTextarea, BModal, BTab, BTabs, FormDrilldown, LoadingSpan, SingleItemSelector };
    }
});
